import {
	getAssessmentModel,
	getAssessmentUiMode,
} from '@/components/assessment/SyllabusAssessment'
import { AssetsProvider } from '@/components/contexts/AssetsProvider'
import * as SyllabusTabPages from '@/components/syllabus-tabs'
import { getOutcomesWithRelatedSyllabusOutcomes } from '@/components/SyllabusView'
import SyllabusViewNew from '@/components/SyllabusViewNew'
import { TAXO_TERM_LIFE_SKILLS } from '@/constants'
import {
	Contentgroup,
	Glossary,
	Syllabus,
	Weblinkext,
	Weblinkvideo,
	WebsiteConfiguration,
} from '@/kontent/content-types'
import { contentTypes } from '@/kontent/project/contentTypes'
import { taxonomies as projectTaxonomies } from '@/kontent/project/taxonomies'
import {
	TaxoKeyLearningArea,
	TaxoStage,
	TaxoStageGroup,
	TaxoStageYear,
} from '@/kontent/taxonomies'
import { SYLLABUS_TABS } from '@/legacy-ported/constants'
import {
	getAllItemsByTypeV2,
	getCachedAssets,
	getItemByCodename,
	getSiteMappings,
	loadWebsiteConfig,
} from '@/lib/api'
import { getAllItemsByType } from '@/lib/getAllItemsByType'
import { getItemsByCodenames } from '@/lib/getItemsByCodenames'
import { NextPageGetLayout, NextPageWithLayout } from '@/pages/_app'
import {
	CommonPageProps,
	CustomSyllabusTab,
	KontentCurriculumCommonResultData,
	TaxoStageWithLifeSkill,
	TaxoStageYearWithLifeSkill,
} from '@/types'
import { FocusareaOrOptionListOrFocusareoptionExtended } from '@/types/customKontentTypes'
import {
	byTaxoCodename,
	convertProjectModelTaxonomiesToElementModelsTaxonomyTerm,
	filterPreviewableSyllabusesOnly,
	getCommonFilteredGlossariesWithFilteredTags,
	getLinkedItems,
	getSyllabusElements,
	getWebLinkWithoutAce,
	isAllowPreviewExternalSyllabus,
	isIntersect,
	isShowPublished,
	isYes,
	redirectTo404,
	redirectToHome,
	uniquePrimitiveArray,
} from '@/utils'
import { shouldHideTabBasedOnCecContentHide } from '@/utils/cec'
import { cleanJson } from '@/utils/cleanJson'
import { getSlugByCodenameYear } from '@/utils/slug-year-utils'
import {
	getStaticPropsForRedirectTo404ForNonPreviewedSyllabus,
	getStaticPropsForRedirectToDefaultFocusArea,
	getSyllabusesResponsesToDetermineDefaultFocusArea,
	isLifeSkillSyllabus,
	isStage6Syllabus,
} from '@/utils/syllabus'
import {
	ElementModels,
	Elements,
	IContentItem,
	IContentItemElements,
	Responses,
} from '@kontent-ai/delivery-sdk'
import {
	GetStaticPaths,
	GetStaticProps,
	GetStaticPropsContext,
	InferGetStaticPropsType,
	PreviewData,
} from 'next'
import { CommonSyllabusPathParams } from './[stage]/[...afterStageSlugs]'

const SyllabusTabPage: NextPageWithLayout = (
	props: InferGetStaticPropsType<typeof getStaticProps>,
) => {
	const SyllabusTabComp =
		SyllabusTabPages[props.params.tab.replace(/-/g, '_')].default

	return (
		<AssetsProvider assets={props.data.assets}>
			<SyllabusTabComp {...props} />
		</AssetsProvider>
	)
}

export type CommonContentTab<T extends IContentItem<IContentItemElements>> =
	KontentCurriculumCommonResultData<T> & {
		syllabus: Responses.IViewContentItemResponse<Syllabus>
		focusArea?: Responses.IViewContentItemResponse<FocusareaOrOptionListOrFocusareoptionExtended>
		years?: ElementModels.TaxonomyTerm<TaxoStageYearWithLifeSkill>[]
		defaultFocusAreaUrls?: Record<
			TaxoStageWithLifeSkill | TaxoStageYearWithLifeSkill,
			string
		>
		webLinkVideos?: Responses.IListContentItemsResponse<Weblinkvideo>
		webLinkExternals?: Responses.IListContentItemsResponse<Weblinkext>
		disabledStages?: Elements.TaxonomyElement<TaxoStage>
		stageOrYears?: ElementModels.TaxonomyTerm<
			TaxoStageWithLifeSkill | TaxoStageYearWithLifeSkill
		>[]
	}

export type RecordFocusAreaPaths = Record<string, { syllabusPaths: string[] }>

export const getStaticPaths: GetStaticPaths = async () => {
	// const syllabusResponse = await getAllItemsByType<Syllabus>({
	// 	type: contentTypes.syllabus.codename,
	// 	depth: 0,
	// 	preview: false,
	// 	allFilter: isAllowPreviewExternalSyllabus()
	// 		? null
	// 		: {
	// 				element: `elements.${contentTypes.syllabus.elements.doredirect.codename}`,
	// 				value: ['no'],
	// 		  },
	// 	elementsParameter: [
	// 		contentTypes.syllabus.elements.key_learning_area__items.codename,
	// 		contentTypes.syllabus.elements.doredirect.codename,
	// 		contentTypes.syllabus.elements.allowpreview.codename,
	// 	],
	// })

	// const paths = syllabusResponse.items
	// 	.filter(filterPreviewableSyllabusesOnly)
	// 	.flatMap((syllabus) => {
	// 		const { key_learning_area__items } = syllabus.elements

	// 		return SYLLABUS_TABS.flatMap((tab) => {
	// 			return key_learning_area__items.value.flatMap(
	// 				(keyLearningArea) => {
	// 					return {
	// 						params: {
	// 							learningarea: getSlugByCodename(
	// 								keyLearningArea.codename,
	// 							),
	// 							syllabus: getSlugByCodename(
	// 								syllabus.system.codename,
	// 							),
	// 							tab:
	// 								tab.id === 'course-overview'
	// 									? 'overview'
	// 									: tab.id,
	// 						},
	// 					}
	// 				},
	// 			)
	// 		})
	// 	})

	return {
		paths: [],
		fallback: 'blocking',
	}
}

const getStageYearsOfStage6Syllabus = (syllabus: Syllabus) => {
	const isStage6Syl = isStage6Syllabus(syllabus)
	if (!isStage6Syl) {
		return []
	}

	const currentStages: TaxoStageWithLifeSkill[] = [
		...syllabus.elements.stages__stages.value.map(byTaxoCodename),
		'life_skills',
	]
	const isLifeSkillsSyl = isLifeSkillSyllabus(syllabus)

	const hasLifeSkillsRelatedSyllabus =
		!!syllabus.elements.relatedlifeskillssyllabus.value.length
	const doesCurrentStageHaveLifeSkills = currentStages.includes('life_skills')
	let stagesOrYears: ElementModels.TaxonomyTerm<TaxoStageYearWithLifeSkill>[] =
		[]

	/**
	 * Add Life Skill "year" accordion if:
	 * 1. the syllabus has life-skills related syllabus, or
	 * 2. the syllabus is a life-skills sylllabus
	 *
	 * and
	 * 3. the "Life skills" stage is selected in the Edit View modal
	 */

	if (!isLifeSkillsSyl && currentStages.includes('stage_6')) {
		stagesOrYears = syllabus.elements.stages__stage_years.value
	}

	if (
		(hasLifeSkillsRelatedSyllabus || isLifeSkillsSyl) &&
		doesCurrentStageHaveLifeSkills
	) {
		stagesOrYears = [...stagesOrYears, TAXO_TERM_LIFE_SKILLS]
	}
	return stagesOrYears
}

export const getCommonSyllabusData = async <
	TContextParams extends CommonSyllabusPathParams = CommonSyllabusPathParams,
	TContext extends GetStaticPropsContext<TContextParams> = GetStaticPropsContext<TContextParams>,
>(
	context: TContext,
	excludeGlossary?: boolean,
) => {
	const { params, preview, previewData } = context
	const { syllabus: syllabusSlug, stage: stageSlug } = params
	let tab = params.tab === 'overview' ? 'course-overview' : params.tab
	const _tab =
		SYLLABUS_TABS.find((t) => t.id === tab)?.id || 'course-overview'

	const isGetPreviewContent = !isShowPublished(previewData) && preview

	let singleSyllabusResponseDepth = 2
	switch (_tab) {
		case 'assessment':
			singleSyllabusResponseDepth = 6
			break
		case 'course-overview':
		case 'outcomes':
			// Outcomes needs syllabus (0) => related life skills syllabus (1) => focus areas (2) => options list (3)
			singleSyllabusResponseDepth = 3
			break
		default:
			singleSyllabusResponseDepth = 2
			break
	}

	const promiseResults = await Promise.all([
		loadWebsiteConfig(isGetPreviewContent),
		getSiteMappings(isGetPreviewContent),
		getAllItemsByType<Syllabus>({
			type: 'syllabus',
			depth: 0,
			elementsParameter: [
				contentTypes.syllabus.elements.title.codename,
				contentTypes.syllabus.elements.key_learning_area__items
					.codename,
				contentTypes.syllabus.elements.key_learning_area_default
					.codename,
				contentTypes.syllabus.elements.syllabus.codename,
				contentTypes.syllabus.elements.doredirect.codename,
				contentTypes.syllabus.elements.allowpreview.codename,
				contentTypes.syllabus.elements.languages.codename,
				contentTypes.syllabus.elements.syllabus_content_model.codename,
				contentTypes.syllabus.elements.publication_year.codename,
				contentTypes.syllabus.elements.has_examples_in.codename,
			],
			allFilter: isAllowPreviewExternalSyllabus()
				? null
				: {
						element: `elements.${contentTypes.syllabus.elements.doredirect.codename}`,
						value: ['no'],
				  },
			preview,
		}),
		getItemByCodename<Syllabus>({
			codename: (syllabusSlug as string).replace(/-/g, '_'),
			preview,
			depth: singleSyllabusResponseDepth,
			elementsParameter: getSyllabusElements([_tab]).filter((field) => {
				// `contentgroup.content_items` is deliberately left out here and
				// fetched separately below, then merged back into `linkedItems`.
				if (
					_tab === 'content' &&
					field ===
						contentTypes.contentgroup.elements.content_items
							.codename
				) {
					return false
				}
				if (_tab === 'content' && !stageSlug) {
					return [
						contentTypes.syllabus.elements.cec_content_hide
							.codename,
						contentTypes.syllabus.elements.cec_print_mode.codename,
						contentTypes.syllabus.elements.syllabus_content_model
							.codename,

						contentTypes.syllabus.elements.seo_description_overview
							.codename,
						contentTypes.syllabus.elements.seo_description_support
							.codename,
						contentTypes.syllabus.elements.seo_description_aim
							.codename,
						contentTypes.syllabus.elements
							.seo_description_assessment.codename,
						contentTypes.syllabus.elements.seo_description_content
							.codename,
						contentTypes.syllabus.elements.seo_description_glossary
							.codename,
						contentTypes.syllabus.elements.seo_description_outcomes
							.codename,
						contentTypes.syllabus.elements.seo_description_overview
							.codename,
						contentTypes.syllabus.elements.seo_description_rationale
							.codename,
						contentTypes.syllabus.elements.seo_description_support
							.codename,

						contentTypes.syllabus.elements.relatedlifeskillssyllabus
							.codename,
						contentTypes.syllabus.elements.stages__stages.codename,
						contentTypes.syllabus.elements.stages__stage_years
							.codename,
						contentTypes.syllabus.elements.implementation_title
							.codename,
						contentTypes.syllabus.elements.implementation_info
							.codename,
						contentTypes.syllabus.elements.implementation_summary
							.codename,
						contentTypes.syllabus.elements.syllabus.codename,
						contentTypes.syllabus.elements.title.codename,
						contentTypes.syllabus.elements.syllabus_type__items
							.codename,
						contentTypes.syllabus.elements.publication_year
							.codename,

						// Extension courses fields
						contentTypes.syllabus.elements.courses.codename,
						contentTypes.course.elements.slug.codename,
						contentTypes.course.elements.display_mode.codename,
						contentTypes.course.elements.content_accordion.codename,
						contentTypes.course.elements.content_accordion_title
							.codename,

						contentTypes.syllabus.elements.doredirect.codename,
						contentTypes.syllabus.elements.allowpreview.codename,

						// KLA fields for canonical URL check
						contentTypes.syllabus.elements.key_learning_area_default
							.codename,
						contentTypes.syllabus.elements.key_learning_area__items
							.codename,
					].includes(field)
				}
				return true
			}),
		}),
		getItemByCodename<WebsiteConfiguration>({
			codename: 'website_configuration',
			depth: 5,
			preview,
		}),
		...(excludeGlossary
			? []
			: [
					getAllItemsByTypeV2<Glossary>({
						type: 'glossary',
						preview,
						depth: 1, // Fetch linkedItems to include custom editor content
						moreQueryFn: (query) => {
							//get global glossary (no `type` and no `syllabus` on the glossary item)
							query.emptyFilter(
								`elements.${contentTypes.glossary.elements.type.codename}`,
							)
							query.emptyFilter(
								`elements.${contentTypes.glossary.elements.syllabus.codename}`,
							)
							return query
						},
					}),
			  ]),
	])

	const config = promiseResults[0]
	const mappings = promiseResults[1]
	const syllabusesResponse = promiseResults[2]
	let syllabusResponse = promiseResults[3]
	const websiteConfigResponse = promiseResults[4]
	const glossariesResponse = excludeGlossary ? null : promiseResults[5]

	if (!syllabusResponse) return null

	// re-attach the content items that the `content_items` exclusion above left
	// out, so every consumer of `syllabusResponse` sees the same shape it always has.
	const contentGroupCodenames = Object.values(syllabusResponse.linkedItems)
		.filter(
			(linkedItem) =>
				linkedItem.system.type === contentTypes.contentgroup.codename,
		)
		.map((linkedItem) => linkedItem.system.codename)

	if (contentGroupCodenames.length) {
		const { items: contentGroups, linkedItems: contentGroupLinkedItems } =
			await getItemsByCodenames<Contentgroup>({
				codenames: contentGroupCodenames,
				// Content groups sit at level 2 of the syllabus tree, so depth 3 here
				// reaches the same leaves a single depth-5 request would have.
				depth: 3,
				elementsParameter: getSyllabusElements([_tab]),
				preview,
			})

		syllabusResponse = {
			...syllabusResponse,
			linkedItems: {
				...syllabusResponse.linkedItems,
				// Order matters: only these copies carry a populated `content_items`
				// element, so they must overwrite the stripped ones from the first call.
				...Object.fromEntries(
					contentGroups.map((contentGroup) => [
						contentGroup.system.codename,
						contentGroup,
					]),
				),
				...contentGroupLinkedItems,
			},
		}
	}

	const syllabusGlossaryResponse = excludeGlossary
		? null
		: await getAllItemsByTypeV2<Glossary>({
				type: 'glossary',
				preview,
				depth: 1, // Fetch linkedItems to include custom editor content
				moreQueryFn: (query) => {
					query.emptyFilter(
						`elements.${contentTypes.glossary.elements.type.codename}`,
					)
					query.anyFilter(
						`elements.${contentTypes.glossary.elements.syllabus.codename}`,
						[
							syllabusResponse.item.elements.syllabus.value[0]
								.codename,
						],
					)
					return query
				},
		  })

	let webLinkVideos: Responses.IListContentItemsResponse<Weblinkvideo>
	let webLinkExternals: Responses.IListContentItemsResponse<Weblinkext>

	const relatedLifeSkillSyllabuses = getLinkedItems(
		syllabusResponse.item.elements.relatedlifeskillssyllabus,
		syllabusResponse.linkedItems,
	)

	// Fetch all Weblinkext/Weblinkvideo for the syllabus
	// - teaching-and-learning tab: needs all items
	// - content tab: needs syllabus-wide items for curriculumWideAssets
	if (_tab === 'teaching-and-learning' || _tab === 'content') {
		const [_webLinkVideos, _webLinkExternals] = await Promise.all([
			getAllItemsByType<Weblinkvideo>({
				type: contentTypes.weblinkvideo.codename,
				preview,
				containsFilter: {
					element: `elements.${contentTypes.weblinkvideo.elements.syllabus.codename}`,
					value: syllabusResponse.item.elements.syllabus.value.map(
						byTaxoCodename,
					),
				},
			}),
			getAllItemsByType<Weblinkext>({
				type: contentTypes.weblinkext.codename,
				preview,
				containsFilter: {
					element: `elements.${contentTypes.weblinkext.elements.syllabus.codename}`,
					value: syllabusResponse.item.elements.syllabus.value.map(
						byTaxoCodename,
					),
				},
			}),
		])
		webLinkVideos = _webLinkVideos
		webLinkExternals = _webLinkExternals
	}
	if (_tab === 'outcomes') {
		// for outcomes tab, if the syllabus is a life-skills syllabus, we need to fetch the related syllabus
		// and get the focus areas from those syllabuses as well

		const isLifeSkillsSyl = isLifeSkillSyllabus(syllabusResponse.item)
		if (isLifeSkillsSyl) {
			// fetch all the syllabuses that are related to the current syllabus
			const parentSyllabusOfLifeskillsSyl =
				await getAllItemsByType<Syllabus>({
					type: 'syllabus',
					preview,
					containsFilter: {
						element: `elements.${contentTypes.syllabus.elements.relatedlifeskillssyllabus.codename}`,
						value: [syllabusResponse.item.system.codename],
					},
					elementsParameter: getSyllabusElements([_tab]),
					depth: 2,
				})

			// include the focus areas of the related syllabuses
			syllabusResponse.item.elements.focus_areas.value =
				uniquePrimitiveArray([
					...syllabusResponse.item.elements.focus_areas.value,
					...parentSyllabusOfLifeskillsSyl.items.flatMap(
						(syl) => syl.elements.focus_areas.value,
					),
				])
			syllabusResponse.linkedItems = {
				...syllabusResponse.linkedItems,
				...parentSyllabusOfLifeskillsSyl.linkedItems,
			}
		} else if (
			isStage6Syllabus(syllabusResponse.item) &&
			syllabusResponse.item.elements.relatedlifeskillssyllabus.value
				?.length
		) {
			const currentStages: TaxoStageWithLifeSkill[] = [
				...syllabusResponse.item.elements.stages__stages.value.map(
					byTaxoCodename,
				),
				'life_skills',
			]

			const outcomes = getOutcomesWithRelatedSyllabusOutcomes(
				syllabusResponse.item,
				currentStages,
				syllabusResponse.linkedItems,
				true,
			)

			syllabusResponse.item.elements.outcomes.value =
				uniquePrimitiveArray([
					...syllabusResponse.item.elements.outcomes.value,
					...outcomes.map((outcome) => outcome.system.codename),
				])

			syllabusResponse.item.elements.focus_areas.value =
				uniquePrimitiveArray([
					...syllabusResponse.item.elements.focus_areas.value,
					...relatedLifeSkillSyllabuses.flatMap(
						(relatedLsSyl) =>
							relatedLsSyl.elements.focus_areas.value,
					),
				])
		}
	}
	// CMS field 'disabled_stages' was removed from wp_homepage and has no replacement.
	// Feature decommissioned - no disabled stages filter applied.
	const disabledStages: Elements.TaxonomyElement<TaxoStage> | undefined =
		undefined
	const syllabus = syllabusResponse.item

	const taxoSyllabusesOfSyllabusAndRelatedSyllabuses = [
		...syllabus.elements.syllabus.value.map(byTaxoCodename),
		...(relatedLifeSkillSyllabuses?.flatMap((relatedSyllabus) =>
			relatedSyllabus.elements.syllabus.value.map(byTaxoCodename),
		) || []),
	]

	let _assets = await getCachedAssets(isGetPreviewContent)

	const assets = _assets.items?.filter((asset) => {
		return (
			asset.elements.syllabus.value.some((taxoSyl) => {
				return taxoSyllabusesOfSyllabusAndRelatedSyllabuses.includes(
					taxoSyl.codename,
				)
			}) &&
			asset.elements.resource_type.value.length &&
			asset.elements.resource_type.value.every(
				(rt) => !rt.codename.includes('ace_'),
			)
		)
	})

	const stages: ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[] = [
		...(convertProjectModelTaxonomiesToElementModelsTaxonomyTerm(
			projectTaxonomies.stage,
		) as ElementModels.TaxonomyTerm<TaxoStage>[]),
		TAXO_TERM_LIFE_SKILLS,
	]

	const years = getStageYearsOfStage6Syllabus(syllabus)

	const stageGroups: ElementModels.TaxonomyTerm<TaxoStageGroup>[] =
		convertProjectModelTaxonomiesToElementModelsTaxonomyTerm(
			projectTaxonomies.stage_group,
		)
	const keyLearningAreas: ElementModels.TaxonomyTerm<TaxoKeyLearningArea>[] =
		convertProjectModelTaxonomiesToElementModelsTaxonomyTerm(
			projectTaxonomies.key_learning_area,
		)

	const defaultFocusAreaUrls = await getDefaultFocusAreaUrls(
		stages,
		years,
		syllabus,
		disabledStages,
		context,
	)
	const stagedAndOrLiveSyllabuses = syllabusesResponse.items.filter(
		filterPreviewableSyllabusesOnly,
	)
	let _glossariesResponse = excludeGlossary
		? null
		: getCommonFilteredGlossariesWithFilteredTags(
				{
					items: [
						...(glossariesResponse?.items || []),
						...(syllabusGlossaryResponse?.items || []),
					],
					linkedItems: {
						...(glossariesResponse?.linkedItems || {}),
						...(syllabusGlossaryResponse?.linkedItems || {}),
					},
				} as Responses.IListContentItemsResponse<Glossary>,
				stagedAndOrLiveSyllabuses,
		  )

	return {
		config,
		mappings,
		syllabusesResponse,
		syllabusResponse,
		syllabus,
		disabledStages,
		stages,
		years,
		stageGroups,
		keyLearningAreas,
		assets,
		glossaries: _glossariesResponse,
		webLinkExternals: webLinkExternals
			? getWebLinkWithoutAce(webLinkExternals)
			: null,
		webLinkVideos: webLinkVideos
			? getWebLinkWithoutAce(webLinkVideos)
			: null,
		defaultFocusAreaUrls,
		websiteConfiguration: websiteConfigResponse,
	}
}

export const getStagesOrYearsBasedOnSyllabusAndDisabledStages = (
	stages: ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[],
	years: ElementModels.TaxonomyTerm<TaxoStageYearWithLifeSkill>[],
	disabledStages: Elements.TaxonomyElement<TaxoStage> | undefined,
	syllabus: Syllabus,
) => {
	const yearsOrStages: ElementModels.TaxonomyTerm<
		TaxoStageWithLifeSkill | TaxoStageYearWithLifeSkill
	>[] = years.length ? years : stages.length ? stages : []

	const _yearsOfStages = yearsOrStages.filter((stageOrYear) => {
		if (stageOrYear.codename === 'life_skills') {
			return isIntersect(
				['stage_4', 'stage_5', 'stage_6'],
				syllabus.elements.stages__stages.value.map(byTaxoCodename),
			)
		}

		if (
			disabledStages?.value
				?.map(byTaxoCodename)
				.includes(stageOrYear.codename as TaxoStage)
		) {
			return false
		}

		return [
			...syllabus.elements.stages__stage_years.value,
			...syllabus.elements.stages__stages.value,
		]
			.map(byTaxoCodename)
			.includes(stageOrYear.codename as TaxoStage)
	})

	return _yearsOfStages
}

type TaxoStageYearExtended = TaxoStageYear & {
	name: string
	codename: string
	isSyllabusCourse?: boolean
	defaultYearCodename?: TaxoStageYear
	courseCodename?: string
}

export const getDefaultFocusAreaUrls = async (
	stages: ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[],
	years: ElementModels.TaxonomyTerm<TaxoStageYearWithLifeSkill>[],
	syllabus: Syllabus,
	disabledStages: Elements.TaxonomyElement<TaxoStage> | undefined,
	context: GetStaticPropsContext<CommonSyllabusPathParams, PreviewData>,
): Promise<CommonContentTab<any>['defaultFocusAreaUrls']> => {
	const _yearsOfStages = getStagesOrYearsBasedOnSyllabusAndDisabledStages(
		stages,
		years,
		disabledStages,
		syllabus,
	)

	const syllabusResponses =
		await getSyllabusesResponsesToDetermineDefaultFocusArea(
			syllabus.system.codename,
			context.preview,
			context.params.stage,
		)

	const syllabusCourseItems = getLinkedItems(
		syllabus.elements.courses,
		syllabusResponses.syllabusesResponse.linkedItems,
	)

	const syllabusStageStages = syllabus.elements.stages__stages.value.map(
		(a) => a.codename,
	)

	const syllabusStageYears = syllabus.elements.stages__stage_years.value.map(
		(a) => a.codename,
	)
	const combinedYearsOfStages = [
		..._yearsOfStages,
		...syllabusCourseItems.flatMap((item) => {
			return [...syllabusStageYears, ...syllabusStageStages].map((a) => {
				const isSyllabusCourse =
					syllabusCourseItems.filter((b) => {
						return (
							isYes(b.elements.display_mode) &&
							b.elements.content_accordion.value[0]?.codename ===
								a
						)
					}).length > 1
				return {
					name: isSyllabusCourse
						? a + '-' + item.elements.slug.value
						: a,
					codename: isSyllabusCourse
						? a + '-' + item.elements.slug.value
						: a,
					defaultYearCodename: a as TaxoStageYear,
					isSyllabusCourse: isSyllabusCourse,
					courseCodename: item.system.codename,
				}
			})
		}),
	] as unknown as TaxoStageYearExtended[]

	const _defaultFocusAreaUrls = await Promise.all(
		combinedYearsOfStages.map(async (stageOrYear) => {
			const { redirect } =
				await getStaticPropsForRedirectToDefaultFocusArea(
					{
						...context,
						params: {
							...context.params,
							stage: getSlugByCodenameYear(stageOrYear.codename),
							isSyllabusCourse: stageOrYear.isSyllabusCourse
								? stageOrYear.isSyllabusCourse.toString()
								: 'false',
							defaultYearCodename:
								stageOrYear.defaultYearCodename,
							courseCodename: stageOrYear.isSyllabusCourse
								? stageOrYear.courseCodename
								: null,
						},
					},
					syllabusResponses,
				)
			return {
				stageOrYearId: stageOrYear.codename,
				defaultFocusAreaUrl: redirect.destination,
			}
		}),
	)

	const result = _defaultFocusAreaUrls.reduce((curr, acc) => {
		return {
			...curr,
			[acc.stageOrYearId]: acc.defaultFocusAreaUrl,
		}
	}, {} as CommonContentTab<any>['defaultFocusAreaUrls'])

	// OC2-6052: Detect single-accordion syllabuses (those that match the CMS
	// `_content_default` redirect rule). Counted by courses with `display_mode`
	// set to "Yes" — default/placeholder courses (e.g. "Further information…")
	// have `display_mode = No` and are excluded. When exactly one such course
	// targets exactly one accordion, expose the redirect URL via a magic key
	// so the SyllabusViewNew Content-tab link can short-circuit the 308.
	const coursesWithContent = syllabusCourseItems.filter((course) =>
		isYes(course.elements.display_mode),
	)
	if (coursesWithContent.length === 1) {
		const accordionValues =
			coursesWithContent[0].elements.content_accordion?.value ?? []
		if (accordionValues.length === 1) {
			const accordionCodename = accordionValues[0]?.codename
			const url = accordionCodename
				? result[accordionCodename]
				: undefined
			if (url) {
				;(result as Record<string, string>)['__content_default__'] = url
			}
		}
	}

	return result
}

export const redirectToOverview = (params: CommonSyllabusPathParams) => {
	return {
		redirect: {
			destination: `/learning-area/${params.learningarea}/${params.syllabus}/overview`,
			permanent: false,
		},
	}
}

export const getStaticProps: GetStaticProps<
	CommonPageProps<
		Syllabus,
		CommonContentTab<Syllabus>,
		CommonSyllabusPathParams
	>,
	CommonSyllabusPathParams
> = async (context) => {
	const { params, preview } = context
	const {
		tab: tabSlug,
		learningarea: learninareaSlug,
		syllabus: syllabusSlug,
	} = params
	const _tab = SYLLABUS_TABS.find((t) => {
		if (tabSlug === 'overview') return t.id === 'course-overview'
		return t.id === tabSlug
	})?.id

	if (!_tab) {
		return redirectToOverview(params)
	}

	const {
		config,
		mappings,
		syllabusesResponse,
		syllabusResponse,
		stages,
		years,
		stageGroups,
		keyLearningAreas,
		assets,
		glossaries,
		webLinkExternals,
		webLinkVideos,
		defaultFocusAreaUrls,
		disabledStages,
		syllabus,
		websiteConfiguration,
	} = (await getCommonSyllabusData(context, _tab !== 'glossary')) || {}

	if (!syllabusResponse) {
		return redirectToHome()
	}
	if (!syllabusResponse.item) {
		return redirectToOverview(params)
	}
	if (
		_tab === 'assessment' &&
		getAssessmentModel(syllabus) === 'v2' &&
		getAssessmentUiMode(syllabus) === 'multi_page_mode'
	) {
		// redirect to the first assessment page
		const assessmentPages = getLinkedItems(
			syllabus.elements.assessments_pages,
			syllabusResponse.linkedItems,
		)

		const slug = assessmentPages?.[0]?.elements?.slug?.value
		if (slug) {
			return {
				redirect: {
					destination: `/learning-areas/${params.learningarea}/${params.syllabus}/assessment/${slug}`,
					permanent: false,
				},
			}
		}
	}

	// redirect to 404 if the syllabus is not allowed to be (pre)viewed
	const redirectPropsIfSyllabusIsNotPublished =
		getStaticPropsForRedirectTo404ForNonPreviewedSyllabus(
			syllabus,
			`/404?path=/learning-areas/${[
				learninareaSlug,
				syllabusSlug,
				tabSlug,
			].join('/')}`,
		)
	if (redirectPropsIfSyllabusIsNotPublished) {
		return redirectPropsIfSyllabusIsNotPublished
	}

	// Check if URL KLA matches canonical KLA
	// Redirect to homepage if using non-canonical KLA
	const canonicalKlaCodename =
		syllabus.elements.key_learning_area_default?.value?.[0]?.codename ||
		syllabus.elements.key_learning_area__items?.value?.[0]?.codename

	const urlKlaCodename = learninareaSlug.replace(/-/g, '_')

	if (canonicalKlaCodename && urlKlaCodename !== canonicalKlaCodename) {
		return redirectTo404(
			`/learning-areas/${[learninareaSlug, syllabusSlug, tabSlug].join(
				'/',
			)}`,
		)
	}

	if (shouldHideTabBasedOnCecContentHide(syllabus, _tab)) {
		return {
			redirect: {
				destination: `/404?path=/learning-areas/${learninareaSlug}/${syllabusSlug}/${tabSlug}`,
				permanent: false,
			},
		}
	}

	const _props: InferGetStaticPropsType<typeof getStaticProps> = {
		mappings,
		preview: !!preview,
		previewData: null,
		params: {
			...params,
			slug: ['learning-areas', params.learningarea, params.syllabus],
		},
		data: {
			config,
			pageResponse: null,
			syllabuses: {
				...syllabusesResponse,
				items: syllabusesResponse.items.filter(
					filterPreviewableSyllabusesOnly,
				),
			},
			syllabus: syllabusResponse,
			focusArea: null,
			stages,
			years,
			stageGroups,
			keyLearningAreas,
			glossaries,
			assets: (
				[
					'course-overview',
					'teaching-and-learning',
					'content',
					'assessment',
				] as CustomSyllabusTab[]
			).includes(_tab)
				? assets
				: [],
			webLinkExternals,
			webLinkVideos,
			defaultFocusAreaUrls,
			disabledStages,
			websiteConfiguration,
		},
	}

	return {
		props: {
			rootLayoutClassName: 'max-w-none mx-0 px-0 !pt-0',
			...cleanJson(_props),
		},
	}
}

export const getLayout: NextPageGetLayout = (
	page,
	serverRouter,
	pageProps: CommonPageProps<Syllabus, CommonContentTab<Syllabus>>,
) => {
	if (serverRouter.isFallback) return <>Fallback...</>
	if (pageProps?.data) {
		const {
			config,
			syllabuses,
			keyLearningAreas,
			stages,
			syllabus,
			focusArea,
			defaultFocusAreaUrls,
		} = pageProps.data
		return (
			<SyllabusViewNew
				config={config}
				mappings={pageProps.mappings}
				linkedItems={{
					...syllabus.linkedItems,
					...(focusArea?.linkedItems || {}),
				}}
				syllabus={syllabus.item}
				allSyllabuses={syllabuses}
				allKeyLearningAreas={keyLearningAreas}
				allStages={stages}
				preview={pageProps.preview}
				defaultFocusAreaUrls={defaultFocusAreaUrls}
			>
				{page}
			</SyllabusViewNew>
		)
	}
	return <>{page}</>
}

SyllabusTabPage.getLayout = getLayout

export default SyllabusTabPage
